/**
 * useGlobalSearchKeyboard — V107.52.34
 *
 * Listens for Cmd+K / Ctrl+K / "/" to open the global search modal.
 * Bails when focus is in any text input so users typing a slash in a
 * textarea / contenteditable get a literal slash, not the search modal.
 *
 * Mounted once inside `GlobalSearchProvider`, which itself is mounted
 * inside OfficeLayout — so the shortcut is automatically scoped to
 * `/home/*` routes and won't fire on tech routes.
 */

import { useEffect } from 'react';

interface Options {
  onOpen: () => void;
  /** Whether the modal is currently open — affects whether we steal Esc */
  isOpen: boolean;
  onClose: () => void;
}

export function useGlobalSearchKeyboard({ onOpen, isOpen, onClose }: Options) {
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!t || !(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      return false;
    }

    function handler(e: KeyboardEvent) {
      // Esc closes the modal when open. We don't read isOpen here directly
      // (effect deps would change every render); the parent passes the
      // current onClose closure which already references state.
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
        return;
      }

      // Cmd+K / Ctrl+K opens the modal — works even from inside an input.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
        return;
      }

      // "/" opens the modal, but only when focus is NOT in a text input.
      if (e.key === '/' && !isTypingTarget(e.target)) {
        e.preventDefault();
        onOpen();
        return;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen, onClose, isOpen]);
}
